import { Switch, Route, useLocation, Redirect } from "wouter";
import { useEffect, type ReactNode } from "react";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/not-found";
import ReactGA from "react-ga4";

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID || "G-W6SYB963R4";
const isProduction = import.meta.env.PROD ||
  window.location.hostname === "socialmedia-a-team.com" ||
  window.location.hostname === "www.socialmedia-a-team.com";

if (isProduction && GA_MEASUREMENT_ID) {
  ReactGA.initialize(GA_MEASUREMENT_ID);
}
import Dashboard from "@/pages/Dashboard";
import Waitlist from "@/pages/waitlist";
import { Content } from "@/pages/Content";
import { ContentLibrary } from "@/pages/ContentLibrary";
import FotoBibliothek from "@/pages/FotoBibliothek";
import EvergreenContent from "@/pages/EvergreenContent";
import IndustryNews from "@/pages/IndustryNews";
import NapkinWisdom from "@/pages/NapkinWisdom";
import PillarPosts from "@/pages/PillarPosts";
import BrandVisuals from "@/pages/BrandVisuals";
import Veroeffentlichungsplan from "@/pages/Veroeffentlichungsplan";
import Wochenplan from "@/pages/Wochenplan";
import RepurposingQuellen from "@/pages/RepurposingQuellen";
import RepurposingContent from "@/pages/RepurposingContent";
import { Goals } from "@/pages/Goals";
import Profil from "@/pages/Profil";
import PasswortAendern from "@/pages/PasswortAendern";
import PlatformConnections from "@/pages/PlatformConnections";
import ATeamBriefing from "@/pages/ATeamBriefing";
import Team from "@/pages/Team";
import MarketingSetup from "@/pages/MarketingSetup";
import MarketingStrategyPage from "@/pages/MarketingStrategy";
import { Landing } from "./components/Landing";
import AdminDashboard from "@/pages/AdminDashboard";
import UploadPage from "@/pages/UploadPage";
import PastorLanding from "@/pages/PastorLanding";
import VideoLanding from "@/pages/VideoLanding";
import Impressum from "@/pages/Impressum";
import Datenschutz from "@/pages/Datenschutz";
import Agb from "@/pages/Agb";
import InstagramSaeulen from "@/pages/InstagramSaeulen";
import InstagramFotos from "@/pages/InstagramFotos";
import InstagramReels from "@/pages/InstagramReels";
import LinkedInBeitraege from "@/pages/LinkedInBeitraege";
import Themenkatalog from "@/pages/Themenkatalog";
import Marke from "@/pages/Marke";
import { EditionModuleGate, EditionModuleRedirect } from "@/components/edition/EditionModuleGate";
import { HotelLockedRoute, HotelSidebarLayout } from "@/components/nav/EditionRouteGuards";
import type { EditionModule } from "@shared/edition";
import LoginPage from "@/pages/LoginPage";
import ResetPassword from "@/pages/ResetPassword";
import { PreviewPage } from "@/modules/preview/components/PreviewPage";
import { SidebarLayout } from "./components/SidebarLayout";
import { useAuth } from "@/hooks/useAuth";
import { useConfig } from "@/hooks/useConfig";
import { needsPayment, resolveEntryScreen, resolveStartPage } from "@/lib/access";
import { CHECKIN_SKIP_KEY } from "@/lib/checkin";
import PaymentStep from "@/pages/PaymentStep";
import CheckIn from "@/pages/CheckIn";
import CheckInSteps from "@/pages/CheckInSteps";
import { Button } from "@/components/ui/button";
import { AccessDeniedModal } from "@/components/AccessDeniedModal";
import { InstallPrompt } from "@/components/InstallPrompt";
import { ServiceWorkerUpdateToast } from "@/components/ServiceWorkerUpdateToast";
import { useAccessError } from "@/lib/accessErrorStore";
import { usePaymentForced } from "@/lib/billingGate";
import { useOAuthCallback } from "@/hooks/useOAuthCallback";
import type { User } from "@shared/schema";

function AppRoutes() {
  const { user, isLoading, isAuthenticated } = useAuth()
  const { config, isLoading: configLoading, paymentRequired } = useConfig()
  const paymentForced = usePaymentForced()
  const [location] = useLocation();
  
  // Track page views with Google Analytics (production only)
  useEffect(() => {
    if (isProduction && GA_MEASUREMENT_ID) {
      ReactGA.send({ hitType: "pageview", page: location });
    }
  }, [location]);
  
  // Handle OAuth callback success/error messages
  useOAuthCallback();

  // DFY tier — same query key the sidebar uses (React Query dedupes, no extra request).
  // Gates the DFY-only /repurposing-quellen route; the server 404s its API for non-DFY
  // tenants but the route must not render a broken shell (audit slice 4).
  // MUST stay above the isLoading early-return below — it's a hook, so a conditional
  // return before it would change the hook count between renders (Rules of Hooks).
  // Not while the user still has to pay: the server access gate answers 402 for every product API, and
  // the global 402 handler would open the AccessDeniedModal on top of the PaymentStep (canary 2026-10-02).
  // Wait for /api/config too: before it loads paymentRequired reads false and the query would race ahead.
  const { data: companyProfile, isError: companyProfileError } = useQuery<{ tier?: string; checklistDismissedAt?: string | null }>({
    queryKey: ['/api/companies/me'],
    enabled: isAuthenticated && config !== undefined && !paymentForced && !needsPayment(user as User | undefined, paymentRequired),
  });
  const isDfy = companyProfile?.tier === 'dfy';

  // Check if current path is a public route that doesn't need auth
  const isPublicRoute = () => {
    const path = window.location.pathname;
    return path.startsWith('/preview/') ||
           path.startsWith('/vsl') ||
           path.startsWith('/landing') ||
           path.startsWith('/login') ||
           path.startsWith('/register') ||
           path.startsWith('/reset-password') ||
           path.startsWith('/impressum') ||
           path.startsWith('/datenschutz') ||
           path.startsWith('/upload/');
  };
  
  // Show loading state while checking authentication (skip for public routes). For a signed-in user also
  // wait for /api/config: until it loads paymentRequired reads false, and an unpaid user would briefly get
  // the wizard (and its gated API calls) instead of the PaymentStep.
  if ((isLoading || (isAuthenticated && configLoading)) && !isPublicRoute()) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-green-50">
        <div className="text-center space-y-4">
          <div className="w-8 h-8 border-2 border-green-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-green-700 font-medium">Lade Ihr A-Team...</p>
        </div>
      </div>
    );
  }

  const userData = user as User | undefined;
  const entryScreen = resolveEntryScreen(isAuthenticated ? userData : undefined, paymentRequired, paymentForced);
  // Signed-in user who is not yet in the app: pay first, then check-in step 1 (never the old wizard).
  const beforeApp = entryScreen === "payment" ? <PaymentStep /> : <CheckIn />;

  // Hotel-Edition module page (PLAN.md §2): the usual login/payment/waitlist checks, then the capability
  // lock — reachable only when the server lists the module in edition.modules, else "/".
  const editionModulePage = (module: EditionModule, page: ReactNode) => {
    if (!isAuthenticated) {
      window.location.href = "/login";
      return null;
    }
    if (entryScreen !== "app") {
      return beforeApp;
    }
    if (userData?.status === 'pending') {
      window.location.href = "/waitlist";
      return null;
    }
    return (
      <SidebarLayout>
        <EditionModuleGate module={module}>{page}</EditionModuleGate>
      </SidebarLayout>
    );
  };

  return (
    <Switch>
      {/* Login Route - Custom branded login page */}
      <Route path="/login">
        <LoginPage />
      </Route>

      {/* Password Reset Route (Public) */}
      <Route path="/reset-password">
        <ResetPassword />
      </Route>

      {/* Onboarding Route */}
      <Route path="/onboarding">
        {() => {
          if (!isAuthenticated) {
            // Website CTAs ("Hi, Boss!") land here: new visitors register, they don't log in.
            window.location.href = "/login?tab=register";
            return null;
          }
          // Same order as "/": pay first, then the check-in; onboarded users go to the start page.
          if (entryScreen === "app") {
            window.location.href = "/";
            return null;
          }
          return beforeApp;
        }}
      </Route>

      {/* Short public sign-up link (website, mails): keeps query params such as ?src= */}
      <Route path="/register">
        {() => {
          const params = new URLSearchParams(window.location.search);
          params.set("tab", "register");
          window.location.replace(`/login?${params.toString()}`);
          return null;
        }}
      </Route>
      
      {/* PASTOR Landing Page Route (Public) */}
      <Route path="/landing">
        <PastorLanding />
      </Route>
      
      {/* Video Sales Letter Landing Page (Public) */}
      <Route path="/vsl">
        <VideoLanding />
      </Route>
      
      {/* Legal Pages (Public) */}
      <Route path="/impressum">
        <Impressum />
      </Route>
      
      <Route path="/datenschutz">
        <Datenschutz />
      </Route>

      <Route path="/agb">
        <Agb />
      </Route>

      {/* Upload Page Route (Public) */}
      <Route path="/upload/:token">
        <UploadPage />
      </Route>

      {/* Preview Pages (Public - Cold Outreach) */}
      <Route path="/preview/:slug">
        <PreviewPage />
      </Route>

      {/* Waitlist Route - for pending users */}
      <Route path="/waitlist">
        {() => {
          if (!isAuthenticated) {
            window.location.href = "/login";
            return null;
          }
          return <Waitlist />;
        }}
      </Route>

      {/* Admin Dashboard Route */}
      <Route path="/admin">
        {() => {
          if (!isAuthenticated) {
            // Redirect to login for admin access
            window.location.href = "/login";
            return null;
          }
          
          // Check if user has admin role
          if (userData?.role !== 'admin') {
            return (
              <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-green-50">
                <div className="text-center space-y-4 p-8">
                  <h1 className="text-2xl font-bold text-slate-900">Zugang verweigert</h1>
                  <p className="text-slate-600">Sie benötigen Administrator-Rechte, um auf diesen Bereich zuzugreifen.</p>
                  <Button onClick={() => window.location.href = "/"} className="mt-4">
                    Zur Startseite
                  </Button>
                </div>
              </div>
            );
          }
          
          return <AdminDashboard />;
        }}
      </Route>
      
      {/* Authenticated user routes with sidebar */}
      <Route path="/">
        {() => {
          if (!isAuthenticated) {
            return <Landing />;
          }

          // STEP 0+1 (resolveEntryScreen): payment gate after register/verify, then check-in step 1, which
          // creates the company. A pending_payment/expired user must subscribe first (access is granted by
          // the verified Stripe webhook, never the client); the server gate enforces regardless.
          if (entryScreen !== "app") {
            return beforeApp;
          }
          
          // STEP 2: After onboarding, check waitlist status - pending users go to waitlist
          if (userData?.status === 'pending') {
            window.location.href = "/waitlist";
            return null;
          }
          
          // STEP 3: the check-in stays the start page until it is hidden or skipped this session
          // (plan Rev. 3 item 27); otherwise the cockpit. Wait for the company so the page does not flip.
          if (companyProfile === undefined && !companyProfileError) {
            return (
              <div className="min-h-screen flex items-center justify-center">
                <div className="animate-pulse text-muted-foreground">Lade Cockpit...</div>
              </div>
            );
          }
          let skipped = false;
          try { skipped = window.sessionStorage.getItem(CHECKIN_SKIP_KEY) === "1"; } catch { /* no storage */ }
          return (
            <SidebarLayout>
              {resolveStartPage(companyProfile, skipped) === "checkin" ? <CheckInSteps /> : <Dashboard />}
            </SidebarLayout>
          );
        }}
      </Route>

      <Route path="/content">
        {() => {
          if (!isAuthenticated) {
            window.location.href = "/login";
            return null;
          }
          if (entryScreen !== "app") {
            return beforeApp;
          }
          if (userData?.status === 'pending') {
            window.location.href = "/waitlist";
            return null;
          }
          return (
            <SidebarLayout>
              <Content />
            </SidebarLayout>
          );
        }}
      </Route>

      <Route path="/inhalts-bibliothek">
        {() => {
          if (!isAuthenticated) {
            window.location.href = "/login";
            return null;
          }
          if (entryScreen !== "app") {
            return beforeApp;
          }
          if (userData?.status === 'pending') {
            window.location.href = "/waitlist";
            return null;
          }
          return (
            <SidebarLayout>
              <ContentLibrary />
            </SidebarLayout>
          );
        }}
      </Route>

      <Route path="/foto-bibliothek">
        {() => {
          if (!isAuthenticated) {
            window.location.href = "/login";
            return null;
          }
          if (entryScreen !== "app") {
            return beforeApp;
          }
          if (userData?.status === 'pending') {
            window.location.href = "/waitlist";
            return null;
          }
          return (
            <SidebarLayout>
              <FotoBibliothek />
            </SidebarLayout>
          );
        }}
      </Route>

      <Route path="/evergreen-content">
        {() => {
          if (!isAuthenticated) {
            window.location.href = "/login";
            return null;
          }
          if (entryScreen !== "app") {
            return beforeApp;
          }
          if (userData?.status === 'pending') {
            window.location.href = "/waitlist";
            return null;
          }
          return (
            <SidebarLayout>
              {/* Extra module: locked for hotels (Mike A3) */}
              <HotelLockedRoute>
                <EvergreenContent />
              </HotelLockedRoute>
            </SidebarLayout>
          );
        }}
      </Route>

      <Route path="/industry-news">
        {() => {
          if (!isAuthenticated) {
            window.location.href = "/login";
            return null;
          }
          if (entryScreen !== "app") {
            return beforeApp;
          }
          if (userData?.status === 'pending') {
            window.location.href = "/waitlist";
            return null;
          }
          return (
            <SidebarLayout>
              {/* Extra module: locked for hotels (Mike A3) */}
              <HotelLockedRoute>
                <IndustryNews />
              </HotelLockedRoute>
            </SidebarLayout>
          );
        }}
      </Route>

      <Route path="/napkin-wisdom">
        {() => {
          if (!isAuthenticated) {
            window.location.href = "/login";
            return null;
          }
          if (entryScreen !== "app") {
            return beforeApp;
          }
          if (userData?.status === 'pending') {
            window.location.href = "/waitlist";
            return null;
          }
          return (
            <SidebarLayout>
              {/* Extra module: locked for hotels (Mike A3) */}
              <HotelLockedRoute>
                <NapkinWisdom />
              </HotelLockedRoute>
            </SidebarLayout>
          );
        }}
      </Route>

      <Route path="/pillar-posts">
        {() => {
          if (!isAuthenticated) {
            window.location.href = "/login";
            return null;
          }
          if (entryScreen !== "app") {
            return beforeApp;
          }
          if (userData?.status === 'pending') {
            window.location.href = "/waitlist";
            return null;
          }
          return (
            <SidebarLayout>
              <PillarPosts />
            </SidebarLayout>
          );
        }}
      </Route>

      <Route path="/instagram-saeulen">
        {() => {
          if (!isAuthenticated) {
            window.location.href = "/login";
            return null;
          }
          if (entryScreen !== "app") {
            return beforeApp;
          }
          if (userData?.status === 'pending') {
            window.location.href = "/waitlist";
            return null;
          }
          // Hotels with the Themenkatalog module live are handed over to it; everyone else as before.
          return (
            <EditionModuleRedirect module="themen" to="/themenkatalog">
              <SidebarLayout>
                <InstagramSaeulen />
              </SidebarLayout>
            </EditionModuleRedirect>
          );
        }}
      </Route>

      {/* Hotel-Edition module pages (PLAN.md §3.1 item 3) — locked by edition.modules. */}
      <Route path="/instagram-fotos">{() => editionModulePage("fotos", <InstagramFotos />)}</Route>
      <Route path="/instagram-reels">{() => editionModulePage("reels", <InstagramReels />)}</Route>
      <Route path="/linkedin-beitraege">{() => editionModulePage("linkedin", <LinkedInBeitraege />)}</Route>
      <Route path="/themenkatalog">{() => editionModulePage("themen", <Themenkatalog />)}</Route>
      <Route path="/marke">{() => editionModulePage("marke", <Marke />)}</Route>

      <Route path="/brand-visuals">
        {() => {
          if (!isAuthenticated) {
            window.location.href = "/login";
            return null;
          }
          if (entryScreen !== "app") {
            return beforeApp;
          }
          if (userData?.status === 'pending') {
            window.location.href = "/waitlist";
            return null;
          }
          return (
            <SidebarLayout>
              {/* Extra module: locked for hotels (Mike A3) */}
              <HotelLockedRoute>
                <BrandVisuals />
              </HotelLockedRoute>
            </SidebarLayout>
          );
        }}
      </Route>

      <Route path="/veroeffentlichungsplan">
        {() => {
          if (!isAuthenticated) {
            window.location.href = "/login";
            return null;
          }
          if (entryScreen !== "app") {
            return beforeApp;
          }
          if (userData?.status === 'pending') {
            window.location.href = "/waitlist";
            return null;
          }
          return (
            <SidebarLayout>
              <Veroeffentlichungsplan />
            </SidebarLayout>
          );
        }}
      </Route>

      <Route path="/wochenplan">
        {() => {
          if (!isAuthenticated) {
            window.location.href = "/login";
            return null;
          }
          if (entryScreen !== "app") {
            return beforeApp;
          }
          if (userData?.status === 'pending') {
            window.location.href = "/waitlist";
            return null;
          }
          return (
            <SidebarLayout>
              <Wochenplan />
            </SidebarLayout>
          );
        }}
      </Route>

      <Route path="/repurposing-quellen">
        {() => {
          if (!isAuthenticated) {
            window.location.href = "/login";
            return null;
          }
          if (entryScreen !== "app") {
            return beforeApp;
          }
          if (userData?.status === 'pending') {
            window.location.href = "/waitlist";
            return null;
          }
          // DFY-tier only. Wait for the tier query before deciding, else a DFY user is
          // bounced during load; a non-DFY user is sent home (the sidebar hides the item,
          // so this only catches direct navigation). On a query error (retry is globally
          // off) fail closed to home rather than spinning forever.
          if (companyProfile === undefined && !companyProfileError) {
            return (
              <div className="min-h-screen flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            );
          }
          if (!isDfy) {
            return <Redirect to="/" />;
          }
          return (
            <SidebarLayout>
              <RepurposingQuellen />
            </SidebarLayout>
          );
        }}
      </Route>

      <Route path="/repurposing">
        {() => {
          if (!isAuthenticated) {
            window.location.href = "/login";
            return null;
          }
          if (entryScreen !== "app") {
            return beforeApp;
          }
          if (userData?.status === 'pending') {
            window.location.href = "/waitlist";
            return null;
          }
          return (
            <SidebarLayout>
              {/* Extra module: locked for hotels (Mike A3) */}
              <HotelLockedRoute>
                <RepurposingContent />
              </HotelLockedRoute>
            </SidebarLayout>
          );
        }}
      </Route>

      <Route path="/ziele">
        {() => {
          if (!isAuthenticated) {
            window.location.href = "/login";
            return null;
          }
          if (entryScreen !== "app") {
            return beforeApp;
          }
          if (userData?.status === 'pending') {
            window.location.href = "/waitlist";
            return null;
          }
          return (
            <SidebarLayout>
              <Goals />
            </SidebarLayout>
          );
        }}
      </Route>

      {/* English alias for Goals */}
      <Route path="/goals">
        {() => {
          if (!isAuthenticated) {
            window.location.href = "/login";
            return null;
          }
          if (entryScreen !== "app") {
            return beforeApp;
          }
          if (userData?.status === 'pending') {
            window.location.href = "/waitlist";
            return null;
          }
          return (
            <SidebarLayout>
              <Goals />
            </SidebarLayout>
          );
        }}
      </Route>

      {/* English alias for Dashboard */}
      <Route path="/dashboard">
        {() => {
          if (!isAuthenticated) {
            window.location.href = "/login";
            return null;
          }
          if (entryScreen !== "app") {
            return beforeApp;
          }
          if (userData?.status === 'pending') {
            window.location.href = "/waitlist";
            return null;
          }
          return (
            <SidebarLayout>
              <Dashboard />
            </SidebarLayout>
          );
        }}
      </Route>

      {/* Cockpit alias for Dashboard */}
      <Route path="/cockpit">
        {() => {
          if (!isAuthenticated) {
            window.location.href = "/login";
            return null;
          }
          if (entryScreen !== "app") {
            return beforeApp;
          }
          if (userData?.status === 'pending') {
            window.location.href = "/waitlist";
            return null;
          }
          return (
            <SidebarLayout>
              <Dashboard />
            </SidebarLayout>
          );
        }}
      </Route>

      <Route path="/team">
        {() => {
          if (!isAuthenticated) {
            window.location.href = "/login";
            return null;
          }
          if (entryScreen !== "app") {
            return beforeApp;
          }
          if (userData?.status === 'pending') {
            window.location.href = "/waitlist";
            return null;
          }
          return (
            <SidebarLayout>
              <Team />
            </SidebarLayout>
          );
        }}
      </Route>

      <Route path="/check-in">
        {() => {
          if (!isAuthenticated) {
            window.location.href = "/login";
            return null;
          }
          if (entryScreen !== "app") {
            return beforeApp;
          }
          return (
            <SidebarLayout>
              <CheckInSteps />
            </SidebarLayout>
          );
        }}
      </Route>

      <Route path="/profil">
        {() => {
          if (!isAuthenticated) {
            window.location.href = "/login";
            return null;
          }
          if (entryScreen !== "app") {
            return beforeApp;
          }
          if (userData?.status === 'pending') {
            window.location.href = "/waitlist";
            return null;
          }
          return (
            <SidebarLayout>
              <Profil />
            </SidebarLayout>
          );
        }}
      </Route>

      <Route path="/a-team-briefing">
        {() => {
          if (!isAuthenticated) {
            window.location.href = "/login";
            return null;
          }
          if (entryScreen !== "app") {
            return beforeApp;
          }
          if (userData?.status === 'pending') {
            window.location.href = "/waitlist";
            return null;
          }
          return (
            <SidebarLayout>
              <ATeamBriefing />
            </SidebarLayout>
          );
        }}
      </Route>

      <Route path="/marketing-setup">
        {() => {
          if (!isAuthenticated) {
            window.location.href = "/login";
            return null;
          }
          if (entryScreen !== "app") {
            return beforeApp;
          }
          if (userData?.status === 'pending') {
            window.location.href = "/waitlist";
            return null;
          }
          return (
            <SidebarLayout>
              <MarketingSetup />
            </SidebarLayout>
          );
        }}
      </Route>

      <Route path="/marketingstrategie">
        {() => {
          if (!isAuthenticated) {
            window.location.href = "/login";
            return null;
          }
          if (entryScreen !== "app") {
            return beforeApp;
          }
          if (userData?.status === 'pending') {
            window.location.href = "/waitlist";
            return null;
          }
          return (
            <SidebarLayout>
              <MarketingStrategyPage />
            </SidebarLayout>
          );
        }}
      </Route>

      <Route path="/strategy">
        {() => {
          if (!isAuthenticated) {
            window.location.href = "/login";
            return null;
          }
          if (entryScreen !== "app") {
            return beforeApp;
          }
          if (userData?.status === 'pending') {
            window.location.href = "/waitlist";
            return null;
          }
          return (
            <SidebarLayout>
              <MarketingStrategyPage />
            </SidebarLayout>
          );
        }}
      </Route>

      <Route path="/passwort">
        {() => {
          if (!isAuthenticated) {
            window.location.href = "/login";
            return null;
          }
          if (entryScreen !== "app") {
            return beforeApp;
          }
          if (userData?.status === 'pending') {
            window.location.href = "/waitlist";
            return null;
          }
          return (
            <SidebarLayout>
              <PasswortAendern />
            </SidebarLayout>
          );
        }}
      </Route>

      {/* Platform Connections Route - after onboarding completion */}
      <Route path="/platform-connections">
        {() => {
          if (!isAuthenticated) {
            window.location.href = "/login";
            return null;
          }
          if (entryScreen !== "app") {
            return beforeApp;
          }
          if (userData?.status === 'pending') {
            window.location.href = "/waitlist";
            return null;
          }
          return <HotelSidebarLayout><PlatformConnections /></HotelSidebarLayout>;
        }}
      </Route>

      {/* German Platform Connections Route - same as above */}
      <Route path="/plattform-verbinden">
        {() => {
          if (!isAuthenticated) {
            window.location.href = "/login";
            return null;
          }
          if (entryScreen !== "app") {
            return beforeApp;
          }
          if (userData?.status === 'pending') {
            window.location.href = "/waitlist";
            return null;
          }
          return <HotelSidebarLayout><PlatformConnections /></HotelSidebarLayout>;
        }}
      </Route>
      
      {/* 404 Fallback */}
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  // Fed globally by the queryClient QueryCache/MutationCache onError (Phase 5 item 4) — any 402/403 from
  // any query/mutation now surfaces this modal instead of a dead shell.
  const { errorState, clearError } = useAccessError();

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <ServiceWorkerUpdateToast />
        <AppRoutes />
        <InstallPrompt />
        
        {/* Global Access Control Error Modal */}
        <AccessDeniedModal
          isOpen={errorState.isOpen}
          onClose={clearError}
          errorCode={errorState.errorCode}
          errorMessage={errorState.errorMessage}
          subscriptionStatus={errorState.subscriptionStatus}
          subscriptionTier={errorState.subscriptionTier}
          expiresAt={errorState.expiresAt}
          upgradeRequired={errorState.upgradeRequired}
          premiumRequired={errorState.premiumRequired}
          supportContact={errorState.supportContact}
        />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;